import { useTranslation } from 'react-i18next';
import { Settings, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { SimulationResponse } from '@/types/simulation';
import { useCatalogLabelMap } from '@/hooks/useCatalog';
import { formatDate } from '@/utils/formatDate';

const statusClassNames: Record<string, string> = {
  Draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  Assigned: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  Completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
};

type Props = {
  simulations: SimulationResponse[];
  loading: boolean;
  onEdit: (sim: SimulationResponse) => void;
  onDelete: (sim: SimulationResponse) => void;
};

export default function SimulationListPreview({ simulations, loading, onEdit, onDelete }: Props) {
  const { t } = useTranslation();
  const typeLabels = useCatalogLabelMap('SimulationType');
  const phaseLabels = useCatalogLabelMap('SalePhase');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (simulations.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center">
        <Settings className="mx-auto mb-3 size-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">{t('configurator.noSimulations')}</p>
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {simulations.map((sim) => {
        const statusTextMap: Record<string, string> = {
          Draft: t('configurator.statusDraft'),
          Assigned: t('configurator.statusAssigned'),
          Completed: t('configurator.statusCompleted'),
        };
        const statusText = statusTextMap[sim.status] ?? statusTextMap.Draft;
        const statusClassName = statusClassNames[sim.status] ?? statusClassNames.Draft;

        return (
          <div
            key={sim.id}
            className="flex items-center gap-4 rounded-xl border border-border bg-card p-4 transition-colors hover:bg-muted"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{sim.name}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {typeLabels.get(sim.simulationType) ?? sim.simulationType} · {phaseLabels.get(sim.salePhase) ?? sim.salePhase} · {formatDate(sim.createdAt)}
              </p>
            </div>
            <Badge variant="outline" className={statusClassName}>
              {statusText}
            </Badge>
            <div className="flex shrink-0 gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-xs" onClick={() => onEdit(sim)}>
                    <Pencil className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('common.edit')}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => onDelete(sim)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('common.delete')}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        );
      })}
    </div>
  );
}
